import { useEffect, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Download, Printer, FileSpreadsheet, CheckCheck, ShieldCheck, FileText, RefreshCw, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import * as XLSX from "xlsx"
import { api } from "@/lib/api"
import { formatDate, LOCALE_MAP } from "@/lib/utils"
import { inputClass, labelClass } from "@/lib/styleClasses"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import type { AdminReservationRow } from "@fohet/caravan-booker-shared/types"
import { isTaxableAge, perGuestTax, nightsInWindow } from "@fohet/caravan-booker-shared/utils/accommodation-tax"
import { describeUbyportError } from "@fohet/caravan-booker-shared/ubyport-error-codes"

interface ReservationListResponse {
    items: AdminReservationRow[]
    total: number
    page: number
    perPage: number
}

const PER_PAGE_OPTIONS = [25, 50, 100, 200] as const
type PerPage = typeof PER_PAGE_OPTIONS[number]
const isPerPage = (n: number): n is PerPage =>
    (PER_PAGE_OPTIONS as readonly number[]).includes(n)

const QUERY_KEY = ["admin", "reservations"]

const FOREIGN_POLICE_EXCLUDE = new Set(["cz"])

interface GuestRow {
    reservationId: string
    arealId: string
    guestIndex: number
    firstName: string
    lastName: string
    dateOfBirth: string
    nationality: string
    documentNumber: string | undefined
    street: string | undefined
    zip: string | undefined
    city: string | undefined
    country: string | undefined
    purposeOfStay: string | undefined
    arrivalDate: string
    departureDate: string
    spotNumber: number | null
    remittedToMunicipality: boolean
    reportedToForeignPolice: boolean
    // Local tourist tax — derived from the reservation's stored
    // `priceBreakdown.taxes` split across taxable guests on that reservation.
    // `taxAmount` is in minor units (haléře). Exempt guests get 0.
    isTaxPayer: boolean
    taxAmount: number
    // Ubyport per-guest tracking — populated from guests jsonb
    ubyportStatus: "none" | "sent" | "error"
    ubyportSubmissionId: string | undefined
    ubyportErrorCodes: string[] | undefined
    // Whether this guest is subject to Ubyport (foreign — only CZ is exempt;
    // SK is reportable per zákon 326/1999 Sb.). Mirrors `isForeignGuest` in
    // the API Ubyport mapper.
    isForeign: boolean
    // Whether this foreign guest has a usable travel-document number. The
    // backend refuses to submit documents shorter than 4 chars, so the UI
    // must not offer them for submission or imply they're pending.
    hasUbyportDocument: boolean
    // Beskydy Card per-guest status — populated from guests jsonb
    bvcStatus: "none" | "preregistered" | "error"
}

// Only Czech citizens are exempt from foreign-police reporting. Slovaks are
// EU citizens but Czech accommodation law (zákon 326/1999 Sb.) still requires
// reporting them — keep this in sync with `isForeignGuest` in the API mapper.
const UBYPORT_EXCLUDE = new Set(["cz", "cze"])

// Mirror of the API's minimum: documents shorter than 4 chars are rejected
// by Ubyport, so the report treats them as "missing document".
const UBYPORT_MIN_DOCUMENT_LENGTH = 4

const buildGuestRows = (
    reservations: AdminReservationRow[],
    from: string,
    to: string,
    foreignPoliceOnly: boolean,
): GuestRow[] => {
    const rows: GuestRow[] = []
    for (const res of reservations) {
        if (res.status === "cancelled") continue
        if (res.departureDate < from || res.arrivalDate > to) continue
        // Per-guest tax for this reservation, pro-rated to the slice of the
        // stay that falls inside [from, to]. A 5-night stay straddling two
        // quarters contributes only the in-window nights to each report —
        // matches the totals on the official quarterly declaration PDF.
        const stayNights = res.nightCount > 0 ? res.nightCount : 1
        const reportableNights = nightsInWindow(res.arrivalDate, res.departureDate, from, to)
        if (reportableNights === 0) continue
        const taxableInRes = res.guests.filter((g) => isTaxableAge(g.dateOfBirth, res.arrivalDate)).length
        const reservationTaxes = (res.priceBreakdown?.taxes ?? 0)
        const proRatedTaxes = Math.round(reservationTaxes * (reportableNights / stayNights))
        const perPayer = perGuestTax(proRatedTaxes, taxableInRes)
        for (let i = 0; i < res.guests.length; i++) {
            const guest = res.guests[i]!
            if (foreignPoliceOnly && FOREIGN_POLICE_EXCLUDE.has(guest.nationality.toLowerCase())) continue
            const payer = isTaxableAge(guest.dateOfBirth, res.arrivalDate)
            rows.push({
                reservationId: res.id,
                arealId: res.arealId,
                guestIndex: i,
                firstName: guest.firstName,
                lastName: guest.lastName,
                dateOfBirth: guest.dateOfBirth,
                nationality: guest.nationality,
                documentNumber: guest.documentNumber,
                street: guest.street,
                zip: guest.zip,
                city: guest.city,
                country: guest.country,
                purposeOfStay: guest.purposeOfStay,
                arrivalDate: res.arrivalDate,
                departureDate: res.departureDate,
                spotNumber: res.spotNumber,
                remittedToMunicipality: guest.remittedToMunicipality ?? false,
                reportedToForeignPolice: guest.reportedToForeignPolice ?? false,
                isTaxPayer: payer,
                taxAmount: payer ? perPayer : 0,
                ubyportStatus: guest.ubyportStatus ?? "none",
                ubyportSubmissionId: guest.ubyportSubmissionId,
                ubyportErrorCodes: guest.ubyportErrorCodes,
                isForeign: !UBYPORT_EXCLUDE.has(guest.nationality.toLowerCase()),
                hasUbyportDocument: (guest.documentNumber?.trim().length ?? 0) >= UBYPORT_MIN_DOCUMENT_LENGTH,
                bvcStatus: guest.bvcStatus ?? "none",
            })
        }
    }
    return rows
}

const CSV_HEADERS = ["First Name", "Last Name", "Date of Birth", "Nationality", "Document Number", "Street", "ZIP", "City", "Country", "Purpose of Stay", "Arrival", "Departure", "Spot", "Tax Payer", "Tax (CZK)", "Remitted", "Foreign Police", "Beskydy Card"]

const formatCzk = (minorUnits: number): string => (minorUnits / 100).toFixed(2)

const bvcStatusText = (s: GuestRow["bvcStatus"]): string =>
    s === "preregistered" ? "preregistered" : s === "error" ? "error" : "no"

const rowToArray = (r: GuestRow): string[] => [
    r.firstName,
    r.lastName,
    r.dateOfBirth,
    r.nationality,
    r.documentNumber ?? "",
    r.street ?? "",
    r.zip ?? "",
    r.city ?? "",
    r.country ?? "",
    r.purposeOfStay ?? "",
    r.arrivalDate,
    r.departureDate,
    r.spotNumber != null ? `#${r.spotNumber}` : "",
    r.isTaxPayer ? "yes" : "no",
    r.isTaxPayer ? formatCzk(r.taxAmount) : "",
    r.remittedToMunicipality ? "yes" : "no",
    r.reportedToForeignPolice ? "yes" : "no",
    bvcStatusText(r.bvcStatus),
]

const fileBaseName = (fromDate: string, toDate: string, foreignPoliceOnly: boolean) =>
    `city-report${foreignPoliceOnly ? "-foreign-police" : ""}-${fromDate}-${toDate}`

const downloadCsv = (rows: GuestRow[], fromDate: string, toDate: string, foreignPoliceOnly: boolean) => {
    const lines = rows.map((r) =>
        rowToArray(r).map((v) => `"${v.replace(/"/g, '""')}"`).join(","),
    )
    const csv = [CSV_HEADERS.join(","), ...lines].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${fileBaseName(fromDate, toDate, foreignPoliceOnly)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

const downloadXlsx = (rows: GuestRow[], fromDate: string, toDate: string, foreignPoliceOnly: boolean) => {
    const data = [CSV_HEADERS, ...rows.map(rowToArray)]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    const sheetName = foreignPoliceOnly ? "Foreign Police" : "City Report"
    XLSX.utils.book_append_sheet(wb, ws, sheetName)
    XLSX.writeFile(wb, `${fileBaseName(fromDate, toDate, foreignPoliceOnly)}.xlsx`)
}

// Unique key for a guest row in the selection set
const rowKey = (r: GuestRow) => `${r.reservationId}:${r.guestIndex}`

// Stream-download a Blob from a fetch Response (we hit /api/... directly so the
// browser handles the file download with the server-supplied Content-Disposition).
const downloadPdf = async (arealId: string, fromDate: string, toDate: string, foreignPoliceOnly: boolean, lang: string, fallbackName: string) => {
    const params = new URLSearchParams({ from: fromDate, to: toDate, foreignOnly: String(foreignPoliceOnly), lang })
    const res = await fetch(`/api/areals/${arealId}/city-report/pdf?${params.toString()}`, { credentials: "include" })
    if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try {
            const body = await res.json()
            if (body && typeof body.error === "string") msg = body.error
        } catch { /* ignore */ }
        throw new Error(msg)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fallbackName
    a.click()
    URL.revokeObjectURL(url)
}

// Quarterly municipal tax declaration — separate endpoint from the city
// report PDF; produces the official "Přiznání k poplatku z pobytu" form.
const downloadTaxDeclaration = async (arealId: string, fromDate: string, toDate: string) => {
    const params = new URLSearchParams({ from: fromDate, to: toDate })
    const res = await fetch(`/api/areals/${arealId}/accommodation-tax/pdf?${params.toString()}`, { credentials: "include" })
    if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try {
            const body = await res.json()
            if (body && typeof body.error === "string") msg = body.error
        } catch { /* ignore */ }
        throw new Error(msg)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `accommodation-tax-${fromDate}-${toDate}.pdf`
    a.click()
    URL.revokeObjectURL(url)
}

// PDFs from the Ubyport confirmation endpoint require cookie auth, so we
// fetch through the same api wrapper and hand the user a blob URL. No
// intermediate download — opens in a new tab.
const openSubmissionPdf = async (submissionId: string, kind: "potvrzeni" | "chyby") => {
    try {
        const res = await fetch(`/api/ubyport/submissions/${submissionId}/${kind}.pdf`, { credentials: "include" })
        if (!res.ok) {
            // Server doesn't store an error PDF when chybyHlavicky is empty
            // (per-record errors come back inline in chybyZaznamu instead).
            // Surface that to the admin instead of the raw 404.
            if (res.status === 404 && kind === "chyby") {
                toast.info("Server nevrátil samostatné PDF s chybami; kódy chyb najdete přímo v badge u hosta.")
                return
            }
            throw new Error(`HTTP ${res.status}`)
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        window.open(url, "_blank")
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
        toast.error((e instanceof Error ? e.message : String(e)))
    }
}

interface UbyportStatusCellProps {
    row: GuestRow
    onRetry: () => void
    retrying: boolean
}

const UbyportStatusCell = ({ row, onRetry, retrying }: UbyportStatusCellProps) => {
    const { t, i18n } = useTranslation()
    if (!row.isForeign) {
        return <span className="text-slate-300 font-bold text-xs">—</span>
    }
    if (row.ubyportStatus === "sent") {
        return (
            <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 border border-green-200 text-xs font-black">
                    {t("admin.cityReport.ubyportSent")}
                </span>
                {row.ubyportSubmissionId && (
                    <button
                        type="button"
                        onClick={() => openSubmissionPdf(row.ubyportSubmissionId!, "potvrzeni")}
                        title={t("admin.cityReport.ubyportOpenPotvrzeni")}
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                    >
                        <FileText className="size-4" />
                    </button>
                )}
            </div>
        )
    }
    if (row.ubyportStatus === "error") {
        const codes = row.ubyportErrorCodes ?? []
        const locale = (i18n.language === "cs" || i18n.language === "pl" ? i18n.language : "en") as "cs" | "en" | "pl"
        const fullList = codes.map((c) => `${c} – ${describeUbyportError(c, locale)}`).join("\n")
        const headlineCode = codes[0]
        const headlineText = headlineCode
            ? `${headlineCode} – ${describeUbyportError(headlineCode, locale)}`
            : t("admin.cityReport.ubyportError")
        return (
            <div className="flex items-center gap-2">
                <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-black max-w-[280px]"
                    title={fullList || undefined}
                >
                    <AlertTriangle className="size-3 shrink-0" />
                    <span className="truncate">{headlineText}</span>
                    {codes.length > 1 && <span className="opacity-60">+{codes.length - 1}</span>}
                </span>
                {row.ubyportSubmissionId && (
                    <button
                        type="button"
                        onClick={() => openSubmissionPdf(row.ubyportSubmissionId!, "chyby")}
                        title={t("admin.cityReport.ubyportOpenChyby")}
                        className="text-slate-400 hover:text-slate-700 transition-colors"
                    >
                        <FileText className="size-4" />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRetry}
                    disabled={retrying}
                    title={t("admin.cityReport.ubyportRetry")}
                    className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-40"
                >
                    <RefreshCw className={`size-4 ${retrying ? "animate-spin" : ""}`} />
                </button>
            </div>
        )
    }
    // status === "none" but no usable document number — the backend would
    // reject this guest, so don't imply a pending submission.
    if (!row.hasUbyportDocument) {
        return (
            <span
                className="inline-block px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-xs font-black"
                title={t("admin.cityReport.ubyportNoDocumentHint")}
            >
                {t("admin.cityReport.ubyportNoDocument")}
            </span>
        )
    }
    // status === "none" — foreign guest that hasn't been submitted yet
    return <span className="text-slate-300 font-bold text-xs">{t("admin.cityReport.ubyportPending")}</span>
}

export const AdminCityReportPage = () => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const queryClient = useQueryClient()
    const { data: areal } = useAdminAreal()

    const today = new Date()
    const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`
    const lastOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate(),
    ).padStart(2, "0")}`

    // Initial state — read from URL so refresh / shareable links work.
    const initialParams = new URLSearchParams(window.location.search)
    const initialPage = Math.max(1, parseInt(initialParams.get("page") ?? "1", 10) || 1)
    const initialPerPageRaw = parseInt(initialParams.get("perPage") ?? "50", 10) || 50
    const initialPerPage: PerPage = isPerPage(initialPerPageRaw) ? initialPerPageRaw : 50

    const storageKey = areal?.id ? `cityReport.filter.${areal.id}` : null
    const loadStored = (): { fromDate: string; toDate: string; foreignPoliceOnly: boolean } | null => {
        if (!storageKey) return null
        try {
            const raw = localStorage.getItem(storageKey)
            if (!raw) return null
            const parsed = JSON.parse(raw)
            if (
                typeof parsed?.fromDate === "string" &&
                typeof parsed?.toDate === "string" &&
                typeof parsed?.foreignPoliceOnly === "boolean"
            ) {
                return parsed
            }
        } catch { /* ignore */ }
        return null
    }
    const stored = loadStored()

    const [fromDate, setFromDate] = useState(initialParams.get("from") ?? stored?.fromDate ?? firstOfMonth)
    const [toDate, setToDate] = useState(initialParams.get("to") ?? stored?.toDate ?? lastOfMonth)
    const [foreignPoliceOnly, setForeignPoliceOnly] = useState(stored?.foreignPoliceOnly ?? false)
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [page, setPage] = useState(initialPage)
    const [perPage, setPerPage] = useState<PerPage>(initialPerPage)

    // Reset to page 1 whenever filters change (otherwise users land on a
    // page that no longer exists for the new filter).
    useEffect(() => { setPage(1) }, [fromDate, toDate])

    // Sync state ↔ URL. Replace (not push) so the back button doesn't
    // walk through every filter tweak.
    useEffect(() => {
        const params = new URLSearchParams()
        if (page > 1) params.set("page", String(page))
        if (perPage !== 50) params.set("perPage", String(perPage))
        if (fromDate !== firstOfMonth) params.set("from", fromDate)
        if (toDate !== lastOfMonth) params.set("to", toDate)
        const search = params.toString()
        const url = search ? `${window.location.pathname}?${search}` : window.location.pathname
        window.history.replaceState(null, "", url)
    }, [page, perPage, fromDate, toDate, firstOfMonth, lastOfMonth])

    // Reload filter from storage when active areal changes
    useEffect(() => {
        if (!storageKey) return
        const s = loadStored()
        setFromDate(s?.fromDate ?? firstOfMonth)
        setToDate(s?.toDate ?? lastOfMonth)
        setForeignPoliceOnly(s?.foreignPoliceOnly ?? false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey])

    // Persist filter changes
    useEffect(() => {
        if (!storageKey) return
        try {
            localStorage.setItem(storageKey, JSON.stringify({ fromDate, toDate, foreignPoliceOnly }))
        } catch { /* ignore */ }
    }, [storageKey, fromDate, toDate, foreignPoliceOnly])

    const { data, isLoading } = useQuery({
        queryKey: [...QUERY_KEY, { page, perPage, from: fromDate, to: toDate, arealId: areal?.id ?? null }],
        queryFn: () => {
            const params = new URLSearchParams({ page: String(page), perPage: String(perPage), from: fromDate, to: toDate })
            if (areal?.id) params.set("arealId", areal.id)
            return api.get<ReservationListResponse>(`/reservations?${params.toString()}`)
        },
        enabled: !!fromDate && !!toDate,
    })

    const reservations = data?.items ?? []
    const total = data?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(total / perPage))
    // Server already filtered by date range; pass identical bounds so the
    // frontend filter is a no-op for date but still applies the foreigner
    // toggle and the cancelled-status skip.
    const guestRows = buildGuestRows(reservations, fromDate, toDate, foreignPoliceOnly)

    // Group selected rows by reservationId → { reservationId: guestIndices[] }
    const remitMutation = useMutation({
        mutationFn: async () => {
            const byReservation = new Map<string, number[]>()
            for (const key of selected) {
                const row = guestRows.find((r) => rowKey(r) === key)
                if (!row) continue
                const existing = byReservation.get(row.reservationId) ?? []
                existing.push(row.guestIndex)
                byReservation.set(row.reservationId, existing)
            }
            await Promise.all(
                Array.from(byReservation.entries()).map(([reservationId, guestIndices]) =>
                    api.patch(`/reservations/${reservationId}/guests/remit`, { guestIndices }),
                ),
            )
        },
        onSuccess: () => {
            setSelected(new Set())
            queryClient.invalidateQueries({ queryKey: ["admin", "city-report"] })
        },
    })

    // Send selected foreign guests to Ubyport. One POST per reservation
    // (the backend takes reservationId + optional guestIndices). Reports
    // individual per-reservation failures via toast rather than failing
    // the whole batch.
    const ubyportMutation = useMutation({
        mutationFn: async () => {
            // Collect unique reservationIds for foreign-guest rows only.
            // All rows share the same arealId (city report is per-camp).
            const reservationIds = new Set<string>()
            let batchArealId: string | null = null
            for (const key of selected) {
                const row = guestRows.find((r) => rowKey(r) === key)
                if (!row || !row.isForeign || !row.hasUbyportDocument) continue
                reservationIds.add(row.reservationId)
                batchArealId ??= row.arealId
            }
            if (!batchArealId || reservationIds.size === 0) return { total: 0, fulfilled: 0 }
            const resp = await api.post<{ results: Array<{ ok: boolean }> }>(
                `/areals/${batchArealId}/ubyport/send-batch`,
                { reservationIds: Array.from(reservationIds) },
            )
            return { total: resp.results.length, fulfilled: resp.results.filter((r) => r.ok).length }
        },
        onSuccess: ({ total: batchTotal, fulfilled }) => {
            setSelected(new Set())
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            if (fulfilled === batchTotal) {
                toast.success(t("admin.cityReport.ubyportBatchOk", { count: batchTotal }))
            } else {
                toast.warning(t("admin.cityReport.ubyportBatchPartial", { ok: fulfilled, total: batchTotal }))
            }
        },
        onError: (e) => {
            toast.error(t("admin.cityReport.ubyportBatchError") + ": " + (e instanceof Error ? e.message : String(e)))
        },
    })

    // Retry a single guest (used on rows already marked "error"). Reuses
    // the per-reservation send endpoint that the batch "Odeslat vybrané"
    // button also drives.
    const retryMutation = useMutation({
        mutationFn: async (row: GuestRow) =>
            api.post(`/areals/${row.arealId}/ubyport/send/${row.reservationId}`, {}),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success(t("admin.cityReport.ubyportRetryOk"))
        },
        onError: (e) => {
            toast.error((e instanceof Error ? e.message : String(e)))
        },
    })

    // How many of the currently selected rows are foreign (= eligible for
    // the Ubyport submit button).
    const selectedForeignCount = Array.from(selected).reduce((n, key) => {
        const row = guestRows.find((r) => rowKey(r) === key)
        return row?.isForeign && row.hasUbyportDocument ? n + 1 : n
    }, 0)

    const toggleRow = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleAll = () => {
        // Select-all covers every row: the same selection drives both the
        // "remit to municipality" and the "send to foreign police" actions,
        // and those are independent of whether a guest is already remitted.
        const allSelected = guestRows.length > 0 && guestRows.every((r) => selected.has(rowKey(r)))
        if (allSelected) {
            setSelected(new Set())
        } else {
            setSelected(new Set(guestRows.map(rowKey)))
        }
    }

    const selectedCount = selected.size

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900">{t("admin.cityReport.title")}</h1>
                <p className="text-slate-500 font-bold mt-1">{t("admin.cityReport.subtitle")}</p>
            </div>

            {/* Filters + actions */}
            <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-6 space-y-4">
                <div className="flex items-end gap-4 flex-wrap">
                    <div>
                        <label className={labelClass}>{t("admin.cityReport.from")}</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.cityReport.to")}</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    {/* Foreign police toggle */}
                    <div className="flex items-center gap-2 pb-px">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <div
                                onClick={() => setForeignPoliceOnly((v) => !v)}
                                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${foreignPoliceOnly ? "bg-brand" : "bg-slate-200"}`}
                            >
                                <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${foreignPoliceOnly ? "translate-x-5" : "translate-x-0"}`} />
                            </div>
                            <span className="text-xs font-black uppercase tracking-widest text-slate-600">
                                {t("admin.cityReport.foreignPoliceFilter")}
                            </span>
                        </label>
                    </div>
                </div>

                {foreignPoliceOnly && (
                    <p className="text-xs text-slate-400 font-bold">{t("admin.cityReport.foreignPoliceHint")}</p>
                )}

                <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-slate-100">
                    <button
                        onClick={() => downloadCsv(guestRows, fromDate, toDate, foreignPoliceOnly)}
                        disabled={guestRows.length === 0}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-40"
                    >
                        <Download className="size-4" />
                        {t("admin.cityReport.exportCsv")}
                    </button>
                    <button
                        onClick={() => downloadXlsx(guestRows, fromDate, toDate, foreignPoliceOnly)}
                        disabled={guestRows.length === 0}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-charcoal text-white font-black text-sm hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-40"
                    >
                        <FileSpreadsheet className="size-4" />
                        {t("admin.cityReport.exportXlsx")}
                    </button>
                    <button
                        onClick={async () => {
                            if (!areal) return
                            try {
                                await downloadPdf(
                                    areal.id,
                                    fromDate,
                                    toDate,
                                    foreignPoliceOnly,
                                    i18n.language === "cs" ? "cs" : "en",
                                    `${fileBaseName(fromDate, toDate, foreignPoliceOnly)}.pdf`,
                                )
                            } catch (e) {
                                toast.error(e instanceof Error ? e.message : t("admin.cityReport.pdfFailed"))
                            }
                        }}
                        disabled={!areal || guestRows.length === 0}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-black text-sm hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-50"
                    >
                        <Printer className="size-4" />
                        {t("admin.cityReport.exportPdf")}
                    </button>
                    {(() => {
                        const billingReady = !!areal?.companyId && !!areal?.billingCompanyName
                        const tooltip = billingReady
                            ? t("admin.cityReport.exportTaxDeclarationHint")
                            : t("admin.cityReport.exportTaxDeclarationDisabled")
                        return (
                            <button
                                onClick={async () => {
                                    if (!areal) return
                                    try {
                                        await downloadTaxDeclaration(areal.id, fromDate, toDate)
                                    } catch (e) {
                                        toast.error(e instanceof Error ? e.message : t("admin.cityReport.pdfFailed"))
                                    }
                                }}
                                disabled={!areal || !billingReady}
                                title={tooltip}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-slate-100 text-slate-600 font-black text-sm hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-50"
                            >
                                <FileText className="size-4" />
                                {t("admin.cityReport.exportTaxDeclaration")}
                            </button>
                        )
                    })()}

                    {selectedCount > 0 && (
                        <div className="flex items-center gap-2 ml-auto">
                            {selectedForeignCount > 0 && (
                                <button
                                    onClick={() => ubyportMutation.mutate()}
                                    disabled={ubyportMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-blue-600 text-white font-black text-sm hover:bg-blue-700 transition-colors active:scale-95 disabled:opacity-60"
                                >
                                    <ShieldCheck className="size-4" />
                                    {t("admin.cityReport.ubyportSendCount", { count: selectedForeignCount })}
                                </button>
                            )}
                            <button
                                onClick={() => remitMutation.mutate()}
                                disabled={remitMutation.isPending}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-[28px] bg-brand text-white font-black text-sm hover:bg-brand-dark transition-colors active:scale-95 disabled:opacity-60"
                            >
                                <CheckCheck className="size-4" />
                                {t("admin.cityReport.markRemittedCount", { count: selectedCount })}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Stats */}
            {(() => {
                const payerRows = guestRows.filter((r) => r.isTaxPayer)
                const payerTotalMinor = payerRows.reduce((acc, r) => acc + r.taxAmount, 0)
                const exemptCount = guestRows.length - payerRows.length
                return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-5">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">{t("admin.cityReport.totalGuests")}</p>
                            <p className="text-3xl font-black text-slate-900">
                                {guestRows.length}
                                {total > reservations.length && (
                                    <span className="text-base font-bold text-slate-400 ml-2">
                                        {t("admin.cityReport.reservationsTotalSuffix", { total })}
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-5">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">{t("admin.cityReport.taxPayers")}</p>
                            <p className="text-3xl font-black text-slate-900">{payerRows.length}</p>
                            <p className="text-xs font-bold text-slate-500 mt-1">{t("admin.cityReport.taxExempt", { count: exemptCount })}</p>
                        </div>
                        <div className="bg-white rounded-[28px] border border-slate-100 shadow-xl p-5">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-1">{t("admin.cityReport.taxTotal")}</p>
                            <p className="text-3xl font-black text-slate-900">{(payerTotalMinor / 100).toLocaleString(locale, { maximumFractionDigits: 2 })} Kč</p>
                        </div>
                        <div className="bg-charcoal rounded-[28px] shadow-2xl p-5">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">{t("admin.cityReport.dateRange")}</p>
                            <p className="text-sm font-black text-white">
                                {fromDate && toDate ? `${formatDate(fromDate, locale)} – ${formatDate(toDate, locale)}` : "—"}
                            </p>
                        </div>
                    </div>
                )
            })()}

            {/* Table */}
            {isLoading ? (
                <div className="text-slate-400 font-bold py-8 text-center">{t("common.loading")}</div>
            ) : guestRows.length === 0 ? (
                <div className="text-center py-16 text-slate-400 font-bold">{t("admin.cityReport.empty")}</div>
            ) : (
                <div className="overflow-x-auto rounded-[28px] border border-slate-200 print:shadow-none">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {/* Select-all checkbox */}
                                <th className="px-4 py-3 w-10 print:hidden">
                                    <input
                                        type="checkbox"
                                        className="rounded accent-brand"
                                        checked={guestRows.length > 0 && guestRows.every((r) => selected.has(rowKey(r)))}
                                        onChange={toggleAll}
                                    />
                                </th>
                                {[
                                    t("admin.cityReport.colFirstName"),
                                    t("admin.cityReport.colLastName"),
                                    t("admin.cityReport.colDob"),
                                    t("admin.cityReport.colNationality"),
                                    t("admin.cityReport.colDocument"),
                                    t("admin.cityReport.colStreet"),
                                    t("admin.cityReport.colZip"),
                                    t("admin.cityReport.colCity"),
                                    t("admin.cityReport.colCountry"),
                                    t("admin.cityReport.colPurpose"),
                                    t("admin.cityReport.colArrival"),
                                    t("admin.cityReport.colDeparture"),
                                    t("admin.cityReport.colSpot"),
                                    t("admin.cityReport.colTaxPayer"),
                                    t("admin.cityReport.colTaxAmount"),
                                    t("admin.cityReport.colRemitted"),
                                    t("admin.cityReport.colUbyport"),
                                    t("admin.cityReport.colBeskydCard"),
                                ].map((col) => (
                                    <th key={col} className="text-left px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-500">
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {guestRows.map((row, i) => {
                                const key = rowKey(row)
                                const isSelected = selected.has(key)
                                return (
                                    <tr
                                        key={key}
                                        className={`border-b border-slate-100 transition-colors ${
                                            isSelected
                                                ? "bg-brand/10"
                                                : i % 2 === 0
                                                    ? "bg-white hover:bg-slate-50"
                                                    : "bg-slate-50/50 hover:bg-slate-100/60"
                                        }`}
                                    >
                                        <td className="px-4 py-3 print:hidden">
                                            <input
                                                type="checkbox"
                                                className="rounded accent-brand"
                                                checked={isSelected}
                                                onChange={() => toggleRow(key)}
                                            />
                                        </td>
                                        <td className="px-4 py-3 font-black text-slate-900">{row.firstName}</td>
                                        <td className="px-4 py-3 font-black text-slate-900">{row.lastName}</td>
                                        <td className="px-4 py-3 font-bold text-slate-600">{row.dateOfBirth}</td>
                                        <td className="px-4 py-3 font-bold text-slate-600 uppercase">{row.nationality}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.documentNumber ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.street ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.zip ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.city ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.country ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-500">{row.purposeOfStay ?? "—"}</td>
                                        <td className="px-4 py-3 font-bold text-slate-600">{formatDate(row.arrivalDate, locale)}</td>
                                        <td className="px-4 py-3 font-bold text-slate-600">{formatDate(row.departureDate, locale)}</td>
                                        <td className="px-4 py-3 font-black text-slate-700">
                                            {row.spotNumber != null ? `#${row.spotNumber}` : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.isTaxPayer ? (
                                                <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs font-black">
                                                    {t("admin.cityReport.taxPayerYes")}
                                                </span>
                                            ) : (
                                                <span className="inline-block px-2 py-0.5 rounded-lg bg-slate-100 text-slate-500 border border-slate-200 text-xs font-bold">
                                                    {t("admin.cityReport.taxPayerNo")}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-black text-slate-700">
                                            {row.isTaxPayer ? `${(row.taxAmount / 100).toLocaleString(locale, { maximumFractionDigits: 2 })} Kč` : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.remittedToMunicipality ? (
                                                <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 border border-green-200 text-xs font-black">
                                                    {t("admin.cityReport.remittedBadge")}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 font-bold text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <UbyportStatusCell
                                                row={row}
                                                onRetry={() => retryMutation.mutate(row)}
                                                retrying={retryMutation.isPending && retryMutation.variables === row}
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.bvcStatus === "preregistered" ? (
                                                <span className="inline-block px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-black">
                                                    {t("admin.cityReport.bvcPreregisteredBadge")}
                                                </span>
                                            ) : row.bvcStatus === "error" ? (
                                                <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-600 border border-red-200 text-xs font-black">
                                                    {t("admin.cityReport.bvcErrorBadge")}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 font-bold text-xs">—</span>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination controls — only render when there is more than one page,
                so a 3-record camp doesn't see an empty pager. */}
            {total > 0 && (
                <div className="flex items-center justify-between gap-4 flex-wrap print:hidden">
                    <div className="flex items-center gap-3">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-500">
                            {t("admin.cityReport.perPageLabel")}
                        </label>
                        <select
                            value={perPage}
                            onChange={(e) => {
                                const v = parseInt(e.target.value, 10)
                                if (isPerPage(v)) {
                                    setPerPage(v)
                                    setPage(1)
                                }
                            }}
                            className="bg-slate-50 border border-slate-200 rounded-2xl py-2 px-3 font-bold text-sm focus:ring-2 focus:ring-brand outline-none"
                        >
                            {PER_PAGE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        <span className="text-sm font-bold text-slate-500">
                            {t("admin.cityReport.paginationRange", {
                                from: (page - 1) * perPage + 1,
                                to: Math.min(page * perPage, total),
                                total,
                            })}
                        </span>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="flex items-center gap-1 px-4 py-2 rounded-2xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="size-4" />
                                {t("admin.cityReport.paginationPrev")}
                            </button>
                            <span className="text-sm font-bold text-slate-600 px-3">
                                {t("admin.cityReport.paginationPage", { page, pages: totalPages })}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="flex items-center gap-1 px-4 py-2 rounded-2xl bg-slate-100 text-slate-600 font-bold text-sm hover:bg-slate-200 transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {t("admin.cityReport.paginationNext")}
                                <ChevronRight className="size-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
